import { ReactElement, useMemo } from 'react'
import { GetServerSidePropsContext } from 'next'
import Head from 'next/head'
import { useTranslations } from 'next-intl'
import { NextPageWithLayout } from '@common/common-types'
import PageLayout from '@components/page-layout/page-layout'
import retriveMessages, { LocaleType } from '@messages/message'
import { CustomerCaseNewInfoData, getCustomerCaseNewInfo } from '@/service/customer-new.service'
import { SEOData } from '@service/seo.service'
import Section1 from './section1'
import Section2 from './section2'
import Section3 from './section3'
import Section4 from './section4'
import { findRegionByLocale } from '@/common/region-locale'
import { withSEO } from '@/common/with-seo'

const CaseStudyDetail: NextPageWithLayout<{ data: CustomerCaseNewInfoData; seoData?: SEOData | null }> = ({
  data: caseDetailData,
  seoData,
}) => {
  const t = useTranslations()

  const section1Data = useMemo(() => {
    return caseDetailData?.topInfo
  }, [caseDetailData])

  const section2Data = useMemo(() => {
    return {
      middleInfo: caseDetailData?.middleInfo,
      cardTitle: caseDetailData?.cardTitle,
      card: caseDetailData?.card,
    }
  }, [caseDetailData])

  const section3Data = useMemo(() => {
    return {
      productTitle: caseDetailData?.productTitle,
      products: caseDetailData?.products,
      downloadTitle: caseDetailData?.downloadTitle,
      downloads: caseDetailData?.downloads,
    }
  }, [caseDetailData])

  const section4Data = useMemo(() => {
    return {
      otherCaseCards: caseDetailData?.otherCaseStudies,
      otherCaseStudiesTitle: caseDetailData?.otherCaseStudiesTitle,
      otherCaseStudiesMoreTitle: caseDetailData?.otherCaseStudiesMoreTitle,
    }
  }, [caseDetailData])

  return (
    <>
      <Head>
        <title>{seoData?.title || section1Data?.tabTitle || section1Data?.title || t('CaseStudy')}</title>
      </Head>
      <div className="w-full h-fit">
        <Section1 data={section1Data} breadcrumbTitle={caseDetailData?.homeTitle} />
        <Section2 data={section2Data} />
        {(!!section3Data?.downloads?.length || !!section3Data?.products?.length) && <Section3 data={section3Data} />}
        {!!section4Data?.otherCaseCards?.length && <Section4 data={section4Data} />}
      </div>
    </>
  )
}

export const getServerSideProps = withSEO(
  async ({ locale, query }: GetServerSidePropsContext) => {
    const messages = retriveMessages(['case-studies'], locale as LocaleType)
    const region = findRegionByLocale(locale as string)

    try {
      const data = await getCustomerCaseNewInfo({ docId: query.caseid as string, region })
      if (!data || (data.hasOwnProperty('code') && data.hasOwnProperty('message'))) {
        return {
          redirect: {
            destination: '/404',
            permanent: false,
          },
        }
      }
      return {
        props: {
          messages,
          data,
          seoData: data?.seoData,
        },
      }
    } catch (error) {
      return {
        redirect: {
          destination: '/404',
          permanent: false,
        },
      }
    }
  },
  ({ query }: GetServerSidePropsContext) => {
    const caseid = (query as { caseid: string }).caseid
    return `/case-studies/${caseid}`
  }
)

CaseStudyDetail.getLayout = function (page: ReactElement, seo) {
  return (
    <PageLayout
      sticky={true}
      pageTitle={seo.title}
      pageDescription={seo.description}
      pageKeywrods={seo.keywords}
      ogtype={'website'}
    >
      {page}
    </PageLayout>
  )
}
export default CaseStudyDetail
